import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import { VimeoUrl } from '@vimeo/player';

import {
  Annotation,
  AnnotatorObject, CommentCategory, CommentHelper, EventType, RichMediaEvent,
  UserTypes
} from 'annotator/annotator-types';
import Modal from 'src/react_components/modal';
import SvgIcon from 'src/react_components/svg-icon';
import { CommentHelper as BellmereCommentHelper } from 'bellmere/types.d';
import QAAlertMessage from './bellmere-qa/qa-alert-message';
import QABooleanButtons from './bellmere-qa/qa-boolean-buttons';
import { BellmereQA } from './bellmere-qa/qa-types.d';
import CommentVideoPlayer from './comment-video-player';

declare const Annotator: AnnotatorObject;

interface CommentHelperListProps {
  annotation: Annotation;
  viewingAsBellmereQa: boolean;
  result: BellmereQA.AnnotationResult
  onBellmereQAResult: (result: Record<string, unknown>) => void;
  bellmereQAEditable: boolean;
}

const CommentHelperList: React.FC<CommentHelperListProps> = ({
  annotation, viewingAsBellmereQa, result, onBellmereQAResult, bellmereQAEditable
}) => {
  const infographicRef = useRef<HTMLImageElement>();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openInfographicModal, setOpenInfographicModal] = useState<boolean>(false);
  const [infographicZoom, setInfographicZoom] = useState<boolean>(false);
  const [current, setCurrent] = useState<CommentHelper>(null);
  const [imgOrientation, setImgOrientation] = useState<'landscape' | 'portrait'>('portrait');

  const imageRef = useCallback((node: HTMLImageElement) => {
    if (node !== null) {
      const { naturalWidth, naturalHeight } = node;
      const aspectRatio = naturalWidth / naturalHeight;

      setImgOrientation(aspectRatio <= 1 ? 'portrait' : 'landscape');
    }
  }, []);

  useEffect(() => {
    if (current?.video) {
      createRichMediaEvent(current.video.id, EventType.Viewed);
    }
  }, [current?.video]);

  useEffect(() => {
    if (current?.infographic) {
      createRichMediaEvent(current.infographic.id, EventType.Viewed);
    }
  }, [current?.infographic]);

  const commentHelpers = annotation.commentHelpers?.filter((commentHelper) => (
    !(commentHelper.hide_comment_type || (commentHelper as BellmereCommentHelper).hideCommentType)
  ));

  if (!commentHelpers || commentHelpers?.length === 0) return null;

  const currentIndex = commentHelpers.indexOf(current);
  const previous = commentHelpers[currentIndex - 1];
  const next = currentIndex > -1 ? commentHelpers[currentIndex + 1] : null;

  const createRichMediaEvent = (richMediaId: number, eventType: EventType) => {
    if (Annotator.userType === UserTypes.Student && richMediaId) {
      $.ajax({
        method: 'POST',
        url: Annotator.data.richMediaEventsPath,
        dataType: 'json',
        data: {
          rich_media_event: {
            submission_annotation_id:
              annotation.comment_type === CommentCategory.CriticalThinking ? null : annotation.id,
            rich_media_id: richMediaId,
            event_type: eventType
          } as RichMediaEvent
        }
      });
    }
  };

  const findQAResult = (commentHelperId: number) => (
    result.areas_for_improvement.find((area) => area.comment_helper_id === commentHelperId)
  );

  const updateBellmereQAResult = (r, commentHelper) => {
    // Find the area for improvement that corresponds to the comment helper
    const area = findQAResult(commentHelper.id) || { comment_helper_id: commentHelper.id, correct: r.correct };

    // Update the correct value
    area.correct = r.correct;

    // Update the area if it already exists, otherwise add it
    onBellmereQAResult({ ...result, areas_for_improvement: [...result.areas_for_improvement, area] });
  };

  const redesignClassName = Annotator.portFairyEnabled && 'mt-2 mb-2 new';
  const gapClassName = !Annotator.portFairyEnabled && 'mb-4';
  const showIdentifiedTitle = annotation.commentCategory !== CommentCategory.TaskAndContent;

  return (
    <div className={`areas-for-improvement d-flex flex-column gap-2 ${redesignClassName}`}>
      {showIdentifiedTitle && (
        <h5 className="m-0">
          {
            annotation.commentCategory === CommentCategory.CriticalThinking ?
              'Demonstrated skill' : 'Identified areas for improvement'
          }
        </h5>
      )}

      {viewingAsBellmereQa && (
        <QAAlertMessage message="Are the below annotation labels correct?" />
      )}

      {/* Comment Helper buttons */}
      <div className={`comment-helpers ${viewingAsBellmereQa ? '' : 'd-flex gap-2'}`}>
        {commentHelpers.map((commentHelper) => (
          !(commentHelper.hide_comment_type || (commentHelper as BellmereCommentHelper).hideCommentType) && (
            <div
              className={`d-flex align-items-center justify-content-between ${gapClassName}`}
              key={`${annotation.id}-${commentHelper.id}`}
            >
              <button
                type="button"
                onClick={() => {
                  setCurrent(commentHelper);
                  setOpenModal(true);
                }}
                className="comment-helper open-rich-media-button d-flex gap-2 align-items-center"
              >
                <div className={`ellipse ${commentHelper?.category?.replace(/_/g, '-')}`} />
                <div>{commentHelper.comment_type || (commentHelper as BellmereCommentHelper).commentType}</div>
              </button>
              {viewingAsBellmereQa && (
                <div className="col text-end">
                  <QABooleanButtons
                    result={result}
                    selected={findQAResult(commentHelper.id)?.correct}
                    buttonKey="correct"
                    buttonText="correct/incorrect"
                    onUpdate={(r) => updateBellmereQAResult(r, commentHelper)}
                    readOnly={!bellmereQAEditable}
                  />
                </div>
              )}
            </div>
          )))}
      </div>

      {/* Comment Helper modal */}
      <Modal
        id="comment-helper-modal"
        className="comment-helper-modal"
        title={current?.comment_type}
        body={(
          <React.Fragment>
            {/* eslint-disable-next-line react/no-danger */}
            <div className="summary" dangerouslySetInnerHTML={{ __html: current?.summary }} />
            {current?.video && (
              <div className="video">
                <h5>{current?.video.description}</h5>
                <CommentVideoPlayer
                  id="comment-video"
                  options={{ url: current.video.video_url as VimeoUrl, width: 662 }}
                  onPlay={() => createRichMediaEvent(current.video.id, EventType.Played)}
                  onEnded={() => createRichMediaEvent(current.video.id, EventType.Ended)}
                />
              </div>
            )}
            {current?.infographic && (
              <div className="infographic">
                <h5>{current?.infographic.description}</h5>
                <button type="button" onClick={() => setOpenInfographicModal(true)}>
                  <img
                    ref={infographicRef}
                    className="comment-infographic-image mw-100 mh-100"
                    src={current?.infographic.image.url}
                    alt={current?.infographic.alt_text}
                    title={current?.infographic.alt_text}
                  />
                </button>
              </div>
            )}
          </React.Fragment>
        )}
        footer={(
          <React.Fragment>
            <div className="col text-start">
              {previous && (
                <button type="button" onClick={() => setCurrent(previous)}>
                  <SvgIcon icon="chevron-left" /> {previous.comment_type}
                </button>
              )}
            </div>
            <div className="col text-center">
              {currentIndex > 0 ? currentIndex + 1 : 1} / {commentHelpers.length}
            </div>
            <div className="col text-end">
              {next && (
                <button type="button" onClick={() => setCurrent(next)}>
                  {next.comment_type} <SvgIcon icon="chevron-right" />
                </button>
              )}
            </div>
          </React.Fragment>
        )}
        show={openModal}
        onClose={() => setOpenModal(false)}
      />

      {/* Infographic modal */}
      {current?.infographic && (
        <Modal
          id="infographic-modal"
          className={`infographic-modal ${imgOrientation}`}
          body={(
            <React.Fragment>
              <button
                type="button"
                aria-label="Close modal dialog"
                className="close border-0"
                onClick={() => setOpenInfographicModal(false)}
              >
                <SvgIcon icon="cancel-circle" className="m-0 w-100 h-100" />
              </button>

              <button
                type="button"
                aria-label="Zoom image"
                className="zoom border-0"
                onClick={() => setInfographicZoom(!infographicZoom)}
              >
                <SvgIcon
                  icon={infographicZoom ? 'zoom_out' : 'zoom_in'}
                  className="m-0 w-100 h-100"
                />
              </button>

              <img
                ref={imageRef}
                className={`${infographicZoom ? 'zoom' : ''} ${imgOrientation}`}
                src={current?.infographic.image.url}
                alt={current?.infographic.alt_text}
              />
            </React.Fragment>
          )}
          show={openInfographicModal}
          onClose={() => setOpenInfographicModal(false)}
          withoutClose
        />
      )}
    </div>
  );
};

export default CommentHelperList;
